import { useI18N } from '@susu/i18n/client'
import { Button, Icon } from '@susu/ui/components'
import { useEffect, useRef } from 'react'

import { ScreenLayout } from '../../components/ScreenLayout'
import {
  NO_PREFERENCE_IMAGE,
  PRE_APPOINTMENT_REDIRECT_MS,
  STOREFRONT_BASE_URL,
} from '../../config/constants'
import { avoEvent } from '../../lib/analytics'
import { emitBookingToolClose } from '../../lib/host-events'
import { useBookingStore } from '../../store'
import { formatAppointmentDateParts } from './formatAppointmentDate'

interface ConfirmationProps {
  onClose?: () => void
}

/**
 * Post-booking / post-reschedule confirmation, plus the brief
 * "Appointment confirmed" bridge into pre-appointment-purpose.
 * Figma (final): https://www.figma.com/design/T6LGX6NvoVoGLqmcvzBqTY/-Dev--Booking-Tool-2.2-Tokenized?node-id=6014-14085
 */
export const Confirmation = ({ onClose }: ConfirmationProps) => {
  const { t } = useI18N()
  const {
    timeslot,
    employeeImage,
    employeeName,
    confirmationVariant,
    language,
    locale,
    bookingId,
    storeName,
    env,
    setState,
  } = useBookingStore()

  const isRescheduled = confirmationVariant === 'rescheduled'
  const isAppointmentConfirmed = confirmationVariant === 'appointment-confirmed'
  const isPreferencesSaved = confirmationVariant === 'preferences-saved'
  const hasRedirectedRef = useRef(false)

  const parts = timeslot ? formatAppointmentDateParts(timeslot, language) : null

  // Once per flow, not once per mount — a `booked` flow mounts this screen
  // twice (`appointment-confirmed`, then `preferences-saved` / `booked` after
  // the purpose screen), and only the first mount is really step 5's view.
  // Reschedule only ever mounts this screen once, so it keeps its single
  // step 5 unchanged.
  useEffect(() => {
    const state = useBookingStore.getState()
    if (state.confirmationStepViewedFlowId === state.flowId) return

    avoEvent('bookingToolStepViewed')
    setState({ confirmationStepViewedFlowId: state.flowId })
  }, [setState])

  // Legacy: after 1500ms, move from the checkmark screen to purpose options.
  useEffect(() => {
    if (!isAppointmentConfirmed || !bookingId || hasRedirectedRef.current) {
      return
    }

    hasRedirectedRef.current = true
    // Fired before setState — both need the still-current `confirmation`
    // screen; firing after would report step 6 (pre-appointment-purpose).
    avoEvent('bookingToolStepCompleted')
    const timer = window.setTimeout(() => {
      setState({
        screen: 'pre-appointment-purpose',
        screenHistory: [],
      })
    }, PRE_APPOINTMENT_REDIRECT_MS)

    return () => {
      window.clearTimeout(timer)
    }
  }, [isAppointmentConfirmed, bookingId, setState])

  const handleDone = () => {
    if (isAppointmentConfirmed) return

    // DOM event for same-document hosts + postMessage for the CRM iframe modal.
    emitBookingToolClose(bookingId)

    if (isRescheduled) {
      // Locale-prefixed both ways: without it the storefront serves its own
      // default market, so a customer who booked from /de-de/ would land on
      // the US store page after rescheduling.
      const base = `${STOREFRONT_BASE_URL[env]}/${locale}`
      const slug = storeName ? storeName.trim().replace(/\s+/g, '-') : null
      window.location.assign(slug ? `${base}/stores/${slug}` : base)
      return
    }
    onClose?.()
  }

  return (
    <ScreenLayout>
      <div className="flex-1 flex flex-col items-center justify-center text-center">
        {isAppointmentConfirmed ? (
          <div className="flex flex-col items-center gap-20 mb-[66px]">
            <span
              className="inline-flex items-center justify-center rounded-full border border-[#E1E1E1] p-8 box-content text-text"
              aria-hidden="true"
            >
              <Icon name="checkmark" size={20} />
            </span>
            <h2
              className="text-[20px] leading-[30px] font-medium tracking-[-0.3px] text-text"
              data-testid="confirmation-heading"
            >
              {t('confirmation.appointmentConfirmedTitle')}
            </h2>
          </div>
        ) : isPreferencesSaved ? (
          <div className="flex flex-col items-center gap-12 text-text max-w-[291px]">
            <h2
              className="text-[24px] leading-[32px] font-medium"
              data-testid="thank-you-heading"
            >
              {t('confirmation.thankYouTitle')}
            </h2>
            <p
              className="text-base leading-[24px] font-light"
              data-testid="thank-you-text"
            >
              {employeeName
                ? t('confirmation.thankYouBodyNamed', { name: employeeName })
                : t('confirmation.thankYouBody')}
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-20">
            <img
              src={employeeImage || NO_PREFERENCE_IMAGE}
              alt={employeeName ?? t('employeesList.noPreference')}
              className="w-120 h-120 rounded-full object-cover"
              data-testid="confirmation-employee-image"
            />
            <div className="flex flex-col items-center gap-12 text-text">
              <h2
                className="text-[20px] leading-[30px] font-medium tracking-[-0.3px]"
                data-testid="confirmation-heading"
              >
                {isRescheduled
                  ? t('confirmation.rescheduledTitle')
                  : t('confirmation.bookedTitle')}
              </h2>
              <p
                className="text-base leading-[24px] font-light max-w-[291px]"
                data-testid="confirmation-body"
              >
                {parts
                  ? t('confirmation.body', {
                      date: parts.date,
                      time: parts.time,
                    })
                  : t('confirmation.emailBody')}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="shrink-0 -mx-16 px-20 pt-20 pb-[calc(20px+env(safe-area-inset-bottom,0px))]">
        <Button
          className="w-full"
          size="l"
          loading={isAppointmentConfirmed}
          onClick={isAppointmentConfirmed ? undefined : handleDone}
          data-testid={`confirmation-button-${employeeName ?? 'done'}`}
        >
          {t('confirmation.done')}
        </Button>
      </div>
    </ScreenLayout>
  )
}
